import React, { useEffect, useRef, useState } from "react";
import {
  ArrowUpRight,
  ChevronUp,
  History,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import ArchiMark from "./ArchiMark";
import { Session } from "./sessions";

/**
 * Archi's conversations as tabs in the bottom-right corner, the way Linear
 * docks its chats: click a tab to open it, click it again to tuck it away,
 * double-click (or right click → Rename) to rename it, × to close it, + for a
 * new one. Closing keeps a conversation in History; Delete removes it for
 * good. With none at all, the dock is just "Ask Archi".
 */

/** Tabs shown in the row; the other open ones wait in History, newest first. */
const VISIBLE_TABS = 4;

interface DockProps {
  sessions: Session[];
  activeId: string | null;
  /** Whether the active conversation's window is open. */
  open: boolean;
  onSelect: (id: string) => void;
  onNew: () => void;
  onRename: (id: string, title: string) => void;
  onClose: (id: string) => void;
  onReopen: (id: string) => void;
  onDelete: (id: string) => void;
}

const isWorking = (s: Session) => s.turns.some((t) => t.streaming);

function TabTitle({
  session,
  editing,
  onDone,
}: {
  session: Session;
  editing: boolean;
  onDone: (title: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(session.title);
  useEffect(() => {
    if (!editing) return;
    setValue(session.title);
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editing, session.title]);

  if (!editing)
    return <span className="min-w-0 truncate">{session.title}</span>;
  return (
    <input
      ref={inputRef}
      value={value}
      aria-label="Conversation name"
      onChange={(e) => setValue(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        // Keys typed while renaming belong to the name, not to the tab.
        e.stopPropagation();
        if (e.key === "Enter") onDone(value);
        if (e.key === "Escape") onDone(null);
      }}
      onBlur={() => onDone(value)}
      className="min-w-0 flex-1 bg-transparent outline-none"
    />
  );
}

const MENU_ITEM =
  "flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-[13px] text-semantic-text-primary hover:bg-semantic-bg-secondary";

interface MenuAction {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
}

/**
 * Right click on a conversation, in the dock or in History: what it can do,
 * in words. Delete is always last and asks once more, in place.
 */
function ConversationMenu({
  at,
  actions,
  onDelete,
  onDismiss,
}: {
  at: { x: number; y: number };
  actions: MenuAction[];
  onDelete: () => void;
  onDismiss: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  useEffect(() => {
    const dismiss = () => onDismiss();
    window.addEventListener("pointerdown", dismiss);
    window.addEventListener("keydown", dismiss);
    return () => {
      window.removeEventListener("pointerdown", dismiss);
      window.removeEventListener("keydown", dismiss);
    };
  }, [onDismiss]);

  return (
    <div
      role="menu"
      className="fixed z-[70] w-44 rounded-lg border border-semantic-border-primary bg-semantic-bg-primary p-1 shadow-lg"
      // Opens upward: the dock sits on the bottom edge.
      style={{ left: at.x, bottom: window.innerHeight - at.y + 6 }}
      onPointerDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {actions.map(({ label, icon: Icon, onClick }) => (
        <button
          key={label}
          type="button"
          role="menuitem"
          className={MENU_ITEM}
          onClick={onClick}
        >
          <Icon
            className="h-3.5 w-3.5 text-semantic-text-secondary"
            aria-hidden
          />
          {label}
        </button>
      ))}
      <div className="my-1 border-t border-semantic-border-primary" />
      <button
        type="button"
        role="menuitem"
        className={`${MENU_ITEM} !text-semantic-text-danger`}
        onClick={() => (confirming ? onDelete() : setConfirming(true))}
      >
        <Trash2 className="h-3.5 w-3.5" aria-hidden />
        {confirming ? "Delete for good?" : "Delete"}
      </button>
    </div>
  );
}

function Tab({
  session,
  active,
  onSelect,
  onRename,
  onClose,
  onDelete,
}: {
  session: Session;
  active: boolean;
  onSelect: () => void;
  onRename: (title: string) => void;
  onClose: () => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const working = isWorking(session);

  return (
    <>
      <div
        role="tab"
        aria-selected={active}
        tabIndex={0}
        title={`${session.title} · ${session.lensLabel}`}
        // The second click of a double click renames; it must not also toggle the window.
        onClick={(e) => {
          if (editing || e.detail > 1) return;
          onSelect();
        }}
        onDoubleClick={() => setEditing(true)}
        onKeyDown={(e) => {
          if (editing) return;
          if (e.key === "Enter") onSelect();
          if (e.key === "F2") setEditing(true);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          setMenu({ x: e.clientX, y: e.clientY });
        }}
        className={`group flex h-9 max-w-[190px] cursor-pointer items-center gap-2 rounded-full border pl-3 pr-1.5 text-[12.5px] font-medium shadow-md transition-colors ${
          active
            ? "border-transparent bg-semantic-bg-inverse-primary text-semantic-text-inverse-primary"
            : "border-semantic-border-primary bg-semantic-bg-primary text-semantic-text-primary hover:bg-semantic-bg-secondary"
        }`}
      >
        <ArchiMark
          size={working ? 14 : 13}
          working={working}
          className="shrink-0"
        />
        <TabTitle
          session={session}
          editing={editing}
          onDone={(title) => {
            setEditing(false);
            if (title !== null) onRename(title);
          }}
        />
        <button
          type="button"
          aria-label={`Close ${session.title}`}
          title="Close (stays in History)"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          // Always there, at the tab's right edge; quiet until pointed at.
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full opacity-60 transition-opacity hover:opacity-100 focus:opacity-100 ${
            active ? "hover:bg-white/15" : "hover:bg-semantic-bg-tertiary"
          }`}
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      {menu && (
        <ConversationMenu
          at={menu}
          onDismiss={() => setMenu(null)}
          actions={[
            {
              label: "Rename",
              icon: Pencil,
              onClick: () => {
                setMenu(null);
                setEditing(true);
              },
            },
            {
              label: "Close",
              icon: X,
              onClick: () => {
                setMenu(null);
                onClose();
              },
            },
          ]}
          onDelete={() => {
            setMenu(null);
            onDelete();
          }}
        />
      )}
    </>
  );
}

/**
 * Every conversation, newest first: the open ones that do not fit in the row,
 * and the closed ones, which reopen on a click or go for good with Delete.
 */
function HistoryMenu({
  sessions,
  overflow,
  onPick,
  onRename,
  onDelete,
}: {
  sessions: Session[];
  /** Open conversations not shown in the row: the button counts them. */
  overflow: number;
  onPick: (session: Session) => void;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [menu, setMenu] = useState<{
    x: number;
    y: number;
    session: Session;
  } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          setConfirming(null);
        }}
        aria-expanded={open}
        aria-label="Conversation history"
        title="History"
        className="flex h-9 items-center gap-1 rounded-full border border-semantic-border-primary bg-semantic-bg-primary px-3 text-[12.5px] font-semibold text-semantic-text-primary shadow-md hover:bg-semantic-bg-secondary"
      >
        {overflow > 0 ? (
          `+${overflow}`
        ) : (
          <History className="h-4 w-4" aria-hidden />
        )}
        <ChevronUp className="h-3.5 w-3.5" aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute bottom-11 right-0 z-[60] max-h-[60vh] w-72 overflow-y-auto rounded-lg border border-semantic-border-primary bg-semantic-bg-primary p-1 shadow-lg"
        >
          <p className="px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-semantic-text-secondary">
            Conversations
          </p>
          {sessions.map((s) => (
            <div
              key={s.id}
              className="group flex items-center gap-1 rounded-md hover:bg-semantic-bg-secondary"
              onContextMenu={(e) => {
                e.preventDefault();
                setMenu({ x: e.clientX, y: e.clientY, session: s });
              }}
            >
              {editingId === s.id ? (
                // Renaming: a field, not a button — an input cannot sit inside one.
                <div className="flex min-w-0 flex-1 flex-col px-2.5 py-1.5">
                  <span className="flex min-w-0 text-[13px] text-semantic-text-primary">
                    <TabTitle
                      session={s}
                      editing
                      onDone={(title) => {
                        setEditingId(null);
                        if (title !== null) onRename(s.id, title);
                      }}
                    />
                  </span>
                  <span className="truncate text-[11px] text-semantic-text-secondary">
                    {s.lensLabel}
                  </span>
                </div>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onPick(s);
                  }}
                  className="flex min-w-0 flex-1 flex-col px-2.5 py-1.5 text-left"
                >
                  <span className="truncate text-[13px] text-semantic-text-primary">
                    {s.title}
                  </span>
                  <span className="truncate text-[11px] text-semantic-text-secondary">
                    {s.closed ? "Closed · " : ""}
                    {s.lensLabel}
                  </span>
                </button>
              )}
              <button
                type="button"
                aria-label={
                  confirming === s.id
                    ? `Delete ${s.title} for good`
                    : `Delete ${s.title}`
                }
                onClick={() => {
                  if (confirming !== s.id) return setConfirming(s.id);
                  setConfirming(null);
                  onDelete(s.id);
                }}
                className={`mr-1 flex h-7 shrink-0 items-center justify-center rounded-md px-1.5 text-[11px] font-semibold transition-opacity ${
                  confirming === s.id
                    ? "bg-semantic-bg-danger-bold text-white opacity-100"
                    : "text-semantic-text-secondary opacity-0 hover:text-semantic-text-danger focus:opacity-100 group-hover:opacity-100"
                }`}
              >
                {confirming === s.id ? (
                  "Delete"
                ) : (
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                )}
              </button>
            </div>
          ))}
        </div>
      )}
      {menu && (
        <ConversationMenu
          at={menu}
          onDismiss={() => setMenu(null)}
          actions={[
            {
              label: menu.session.closed ? "Reopen" : "Open",
              icon: ArrowUpRight,
              onClick: () => {
                setMenu(null);
                setOpen(false);
                onPick(menu.session);
              },
            },
            {
              label: "Rename",
              icon: Pencil,
              onClick: () => {
                setMenu(null);
                setEditingId(menu.session.id);
              },
            },
          ]}
          onDelete={() => {
            setMenu(null);
            onDelete(menu.session.id);
          }}
        />
      )}
    </div>
  );
}

const ArchiDock: React.FC<DockProps> = ({
  sessions,
  activeId,
  open,
  onSelect,
  onNew,
  onRename,
  onClose,
  onReopen,
  onDelete,
}) => {
  if (sessions.length === 0) {
    return (
      <div className="fixed bottom-3 right-3 z-50">
        <button
          type="button"
          onClick={onNew}
          aria-label="Ask Archi"
          // A circle on a phone: the full pill covered the list pager's
          // buttons at the bottom of the screen (CTX-1383).
          className="flex h-10 w-10 items-center justify-center gap-2 rounded-full bg-semantic-bg-inverse-primary text-[13px] font-semibold text-semantic-text-inverse-primary shadow-lg transition-transform hover:-translate-y-0.5 sm:w-auto sm:justify-start sm:pl-3.5 sm:pr-4"
        >
          <ArchiMark size={16} />
          <span className="hidden sm:inline">Ask Archi</span>
        </button>
      </div>
    );
  }

  // Newest first, in the order they were started: a tab never moves while
  // Archi answers in it. The open one always stays in the row.
  const docked = sessions.filter((s) => !s.closed);
  const shown = docked.slice(0, VISIBLE_TABS);
  const active = docked.find((s) => s.id === activeId);
  if (active && !shown.includes(active)) shown[shown.length - 1] = active;
  const overflow = docked.filter((s) => !shown.includes(s)).length;

  return (
    <div
      role="tablist"
      aria-label="Archi conversations"
      className="fixed bottom-3 right-3 z-50 flex items-center gap-1.5"
    >
      <HistoryMenu
        sessions={sessions}
        overflow={overflow}
        // From History a pick always opens it — never the tab's "click again to tuck away".
        onPick={(s) => onReopen(s.id)}
        onRename={onRename}
        onDelete={onDelete}
      />
      {/* Oldest of the row on the left, newest next to +. */}
      {[...shown].reverse().map((s) => (
        <Tab
          key={s.id}
          session={s}
          active={open && s.id === activeId}
          onSelect={() => onSelect(s.id)}
          onRename={(title) => onRename(s.id, title)}
          onClose={() => onClose(s.id)}
          onDelete={() => onDelete(s.id)}
        />
      ))}
      <button
        type="button"
        onClick={onNew}
        aria-label="New conversation"
        className="flex h-9 w-9 items-center justify-center rounded-full bg-semantic-bg-inverse-primary text-semantic-text-inverse-primary shadow-md transition-transform hover:-translate-y-0.5"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
};

export default ArchiDock;
